import classNames from 'clsx';
import type { AnyObject } from 'final-form';
import React from 'react';
import type { FieldMetaState } from 'react-final-form';
import { Field } from 'react-final-form';

import Tick from '@swf/core/assets/icons/tick.svg';
import type { InjectedValidatorProps } from '@swf/core/hoc/withValidator';
import withValidator from '@swf/core/hoc/withValidator';
import type { LabelProp } from '@swf/core/hooks/useDisplayLabel';
import useDisplayLabel from '@swf/core/hooks/useDisplayLabel';
import usePauseValidator from '@swf/core/hooks/usePauseValidator';
import useTrackers from '@swf/core/hooks/useTrackers';
import useTrackingLabel from '@swf/core/hooks/useTrackingLabel';

import styles from './Toggle.scss';

export type RawProps = InjectedValidatorProps & {
  id: string;
  label?: React.ReactNode;
  error?: boolean;
  type: 'radio' | 'switch' | 'checkbox';
  name?: string;
  value?: string | boolean;
  checked?: boolean;
  className?: string;
  hideLabel?: boolean;
  onOffToggle?: boolean;
  align?: 'left' | 'right';
  variant?: 'primary' | 'secondary';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta?: FieldMetaState<any>;
} & React.InputHTMLAttributes<HTMLInputElement>;

export interface Props extends Omit<RawProps, 'label'> {
  label: LabelProp;
}

export const RawToggle: React.FC<RawProps> = ({
  label,
  hideLabel,
  meta,
  error,
  onOffToggle,
  id,
  checked,
  type,
  align = 'left',
  variant = 'primary',
  className,
  ...rest
}) => {
  const isSwitch = type === 'switch';
  const alignRight = align === 'right' && isSwitch;

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.error]: (meta && meta.touched && (meta.error || meta.submitError)) || error,
        [styles[variant]]: variant,
        [styles.alignRight]: alignRight
      })}
    >
      <input
        {...rest}
        id={id}
        checked={checked}
        type={isSwitch ? 'checkbox' : type}
        className={classNames(styles.input, {
          [styles.toggleSwitch]: isSwitch,
          [styles.onOffToggle]: isSwitch && onOffToggle
        })}
      />
      <label
        htmlFor={id}
        className={classNames(
          styles.label,
          {
            [styles.switchLabel]: isSwitch,
            [styles.hidden]: hideLabel,
            [styles.emptyLabel]: !label
          },
          className
        )}
      >
        {isSwitch && <span className={styles.toggle} />}
        {type === 'checkbox' && <Tick />}
        {hideLabel || variant === 'secondary' ? <span>{label}</span> : label}
      </label>
    </div>
  );
};

const ToggleAdapter = ({ input, meta, label, onChange, uiType, ...rest }: AnyObject) => {
  const { inputFilled } = useTrackers();
  const displayLabel = useDisplayLabel(label);
  const fieldName = useTrackingLabel(label);

  return (
    <RawToggle
      {...rest}
      {...input}
      type={uiType}
      label={displayLabel}
      onChange={(event) => {
        inputFilled({ fieldName, fieldType: input.type, boolInput: event.target.checked });
        input.onChange?.(event);
        onChange?.(event);
      }}
      meta={meta}
    />
  );
};

const Toggle: React.FC<Props> = ({ validator, validationErrors, type, ...rest }) => {
  usePauseValidator();

  return (
    <Field
      {...rest}
      type={type === 'switch' ? 'checkbox' : type}
      uiType={type}
      name={rest.name ? rest.name : rest.id}
      component={ToggleAdapter}
      validate={validator}
    />
  );
};

export default withValidator(Toggle);
